import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Branch master (Masters → Branches). A physical/legal office used for
 * invoicing and as the home office of users, customers, and leads. Carries a
 * GST registration and an address resolved against the geography masters
 * (country → state → city). See ADR-0016 / ADR-0020.
 */

const CountryRefSchema = z.object({ id: UuidSchema, isoCode: z.string(), name: z.string() });
const StateRefSchema = z.object({ id: UuidSchema, name: z.string() });
const CityRefSchema = z.object({ id: UuidSchema, name: z.string() });

export const BranchListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  gstin: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  /** Short badge for the website "Our locations" card (e.g. "Head Office"). */
  officeBadge: z.string().nullable(),
  /** Google Maps embed (full <iframe> HTML or just its src URL). */
  mapEmbedUrl: z.string().nullable(),
  city: CityRefSchema.nullable(),
  state: StateRefSchema.nullable(),
  country: CountryRefSchema.nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type BranchListItem = z.infer<typeof BranchListItemSchema>;

export const BranchListQuerySchema = PaginationQuerySchema.extend({
  countryId: UuidSchema.optional(),
  stateId: UuidSchema.optional(),
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type BranchListQuery = z.infer<typeof BranchListQuerySchema>;

export const BranchListResponseSchema = paginatedResponseSchema(BranchListItemSchema);
export type BranchListResponse = z.infer<typeof BranchListResponseSchema>;

export const BranchDetailSchema = BranchListItemSchema.extend({
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  postalCode: z.string().nullable(),
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type BranchDetail = z.infer<typeof BranchDetailSchema>;

const BranchCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(160),
  /** GSTIN / state registration for invoicing. */
  gstin: z.string().trim().max(20).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  email: z.string().trim().email('Enter a valid email').max(200).nullable().optional(),
  /** Website "Our locations" card badge, e.g. "Head Office". */
  officeBadge: z.string().trim().max(60).nullable().optional(),
  /** Google Maps embed: full <iframe> HTML or just the src URL. */
  mapEmbedUrl: z.string().trim().max(2000).nullable().optional(),
  addressLine1: z.string().trim().max(200).nullable().optional(),
  addressLine2: z.string().trim().max(200).nullable().optional(),
  countryId: UuidSchema.nullable().optional(),
  stateId: UuidSchema.nullable().optional(),
  cityId: UuidSchema.nullable().optional(),
  postalCode: z.string().trim().max(16).nullable().optional(),
  legacyId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateBranchRequestSchema = BranchCoreFieldsSchema;
export type CreateBranchRequest = z.infer<typeof CreateBranchRequestSchema>;

export const UpdateBranchRequestSchema = BranchCoreFieldsSchema.partial();
export type UpdateBranchRequest = z.infer<typeof UpdateBranchRequestSchema>;
